import React, { forwardRef, memo } from 'react';
import {
    TextareaAutosizeProps,
    TextareaHeightChangeMeta
} from 'react-textarea-autosize';
import { ICustomThemeInput } from '../HBTextField/HBTextField.types';
import { StyledTextareaAutosize } from './HBAutosizeTextArea.styles';
import { TTextFieldSize } from '../HBTextField/variables';
import { stopEventPropagation } from '../../utils/shared.utils';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';

export interface HBAutosizeTextAreaProps
    extends Omit<TextareaAutosizeProps, 'size' | 'as' | 'ref'> {
    /** Overrides default visual styling with custom CSS values for background, border, font, etc. */
    customTheme?: ICustomThemeInput;
    /** Controls the font size of the textarea; maps to TTextFieldSize variants. */
    size?: TTextFieldSize;
    /** CSS class applied to the root element. */
    className?: string;
    testId?: string;
    /** HTML `name` attribute forwarded to the underlying `<textarea>` element; also used as the test ID identifier when `testId` and `testIdName` are omitted. */
    name?: string;
    /** Minimum number of visible rows when the textarea is empty or has little content. */
    minRows?: number;
    /** Maximum number of visible rows before the textarea stops growing and scrolls instead. */
    maxRows?: number;
    /** Called whenever the textarea height changes; receives the new height in pixels and change metadata. */
    onHeightChange?: (height: number, meta: TextareaHeightChangeMeta) => void;
    /** When true, caches DOM measurements to avoid re-measuring on every resize; improves performance for frequently resizing textareas. */
    cacheMeasurements?: boolean;
}

const HBAutosizeTextArea = forwardRef<
    HTMLTextAreaElement,
    HBAutosizeTextAreaProps & MustIncludeOneOfTestIdProps
>(
    (
        {
            value,
            placeholder = 'Type something here',
            autoFocus,
            onChange,
            onFocus,
            onBlur,
            onKeyUp,
            onKeyDown,
            customTheme,
            onPaste,
            size,
            className,
            id,
            name,
            testIdName,
            testId,
            ...baseProps
        },
        ref
    ) => {
        const finalTestId = useBuildTestIdInternal({
            testId,
            identifier: testIdName || id || name,
            suffix: 'input',
            componentName: 'HBAutosizeTextArea'
        });

        return (
            <StyledTextareaAutosize
                {...baseProps}
                id={id}
                name={name}
                ref={ref}
                className={className}
                value={value}
                onPaste={onPaste || stopEventPropagation}
                placeholder={placeholder}
                autoFocus={autoFocus}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyUp={onKeyUp}
                onKeyDown={onKeyDown}
                $customTheme={customTheme}
                fontSize={size}
                data-testid={finalTestId}
                autoComplete="off"
            />
        );
    }
);

export default memo(HBAutosizeTextArea);
